import { useEffect, useMemo, useRef, useState, Fragment } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Table, Plus, Copy, Trash, Download, Calculator, CaretDown, CaretRight, Upload, FileArrowDown, FloppyDisk, ArrowClockwise, X } from "@phosphor-icons/react";
import RowExpanded from "@/components/batch/RowExpanded";
import { Th, Td, LabeledInput, BulkValueEditor, ResultCell } from "@/components/batch/primitives";
import { CellInput, CellSelect } from "@/components/batch/primitives";
import { buildTemplateCsvBlob, parseCsvToRows, buildResultsCsvBlob, downloadBlob } from "@/components/batch/csvHandlers";
import {
  CIRCUIT_TYPES, LOAD_UNITS, TEMP_RATINGS, MEDIA_OPTIONS, CONDUIT_MATERIALS,
  BULK_COLUMNS, CSV_COLUMNS, CSV_NUMERIC, CSV_BOOL,
  DEFAULT_ROWS, blankRow, parseBool, parseCSV,
} from "@/components/batch/constants";

/* ============================================================
   BATCH CONDUCTOR SIZING · v2.75 (+ v2.76 bulk-edit + CSV import,
   v2.77 results-CSV, v2.78 save/load, v2.79 deep-link, v2.85 refactor)
   ------------------------------------------------------------
   Spreadsheet-style editor for computing many circuit conductor
   sizes in one shot, backed by:
     POST /api/calculators/conductor-sizing/batch
     POST /api/calculators/conductor-sizing/batch-pe-sheet
     POST /api/calculators/conductor-sizing/batches       (save)
     GET  /api/calculators/conductor-sizing/batches?…     (list)
     GET  /api/calculators/conductor-sizing/batches/:id   (fetch)
     DELETE /api/calculators/conductor-sizing/batches/:id (delete)
   All circuit types supported per row: feeder | motor_branch |
   transformer | motor_group. Row-level constants + the mode-specific
   expanded editor + CSV helpers live in ./batch/*.
   ============================================================ */


export default function BatchConductor({ autoLoadBatchId = "" } = {}) {
  const [rows, setRows] = useState(() =>
    Array.from({ length: DEFAULT_ROWS }, (_, i) => blankRow(i + 1)),
  );
  const [expandedIdx, setExpandedIdx] = useState(null);
  const [results, setResults] = useState({}); // idx -> result object
  const [busy, setBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [cableTypes, setCableTypes] = useState([]);
  const [projectName, setProjectName] = useState("");
  const [engineerName, setEngineerName] = useState("");
  const [engineerPeLicense, setEngineerPeLicense] = useState("");
  // Once the user has computed once, keep result column visible; invalidate
  // per-row when its inputs change.
  const [hasComputed, setHasComputed] = useState(false);

  // v2.76 · row-selection + bulk-edit + CSV import.
  const [selected, setSelected] = useState(() => new Set());
  const [bulkColumn, setBulkColumn] = useState(BULK_COLUMNS[0].key);
  const [bulkValue, setBulkValue] = useState(BULK_COLUMNS[0].options?.[0]?.value ?? "");
  const csvInputRef = useRef(null);

  // v2.78 · project persistence — save + load + delete saved batches.
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [savedBatches, setSavedBatches] = useState([]);
  const [savingBatch, setSavingBatch] = useState(false);
  const [loadingBatchId, setLoadingBatchId] = useState("");

  useEffect(() => {
    api.get("/calculators/cable-types").then(({ data }) => setCableTypes(data)).catch(() => {});
    // v2.78 · project list for save/load pickers
    api.get("/projects").then(({ data }) => setProjects(data)).catch(() => {});
  }, []);

  // v2.78 · Refresh saved batches whenever the picked project changes.
  const refreshSavedBatches = async (pid) => {
    if (!pid) { setSavedBatches([]); return; }
    try {
      const { data } = await api.get(
        `/calculators/conductor-sizing/batches?project_id=${encodeURIComponent(pid)}`,
      );
      setSavedBatches(data);
    } catch {
      setSavedBatches([]);
    }
  };
  useEffect(() => { refreshSavedBatches(projectId); }, [projectId]);

  // v2.76 · Reset bulkValue whenever the bulk column changes so the input
  // widget always shows a sane default for its type.
  useEffect(() => {
    const col = BULK_COLUMNS.find((c) => c.key === bulkColumn);
    if (!col) return;
    if (col.type === "select") setBulkValue(col.options?.[0]?.value ?? "");
    else if (col.type === "cable") setBulkValue(cableTypes[0]?.key ?? "THWN-2");
    else if (col.type === "boolean") setBulkValue(false);
    else setBulkValue("");
  }, [bulkColumn, cableTypes]);

  const setCell = (idx, patch) => {
    setRows((prev) => {
      const next = prev.map((r, i) => (i === idx ? { ...r, ...patch } : r));
      return next;
    });
    // any input change on a row invalidates that row's result
    setResults((prev) => {
      if (prev[idx] == null) return prev;
      const next = { ...prev };
      delete next[idx];
      return next;
    });
  };

  const addRow = () =>
    setRows((prev) => [...prev, blankRow(prev.length + 1)]);

  const duplicateRow = (idx) =>
    setRows((prev) => {
      const clone = { ...prev[idx], tag: `${prev[idx].tag}-copy` };
      const next = [...prev];
      next.splice(idx + 1, 0, clone);
      // Shift result indices right for any row ≥ idx+1 so results still
      // line up with their originating row. The freshly-inserted clone
      // starts with no result — user must re-Compute to size it.
      setResults((prevRes) => {
        const shifted = {};
        Object.entries(prevRes).forEach(([k, v]) => {
          const ki = Number(k);
          if (ki <= idx) shifted[ki] = v;
          else shifted[ki + 1] = v;
        });
        return shifted;
      });
      // v2.76 · shift selection alongside the results map.
      setSelected((prevSel) => {
        const shifted = new Set();
        prevSel.forEach((i) => {
          if (i <= idx) shifted.add(i);
          else shifted.add(i + 1);
        });
        return shifted;
      });
      return next;
    });

  // Row-remove should also purge selection.
  const removeRow = (idx) => {
    if (rows.length <= 1) return;
    setRows((prev) => prev.filter((_, i) => i !== idx));
    setResults((prev) => {
      const next = {};
      Object.entries(prev).forEach(([k, v]) => {
        const ki = Number(k);
        if (ki < idx) next[ki] = v;
        else if (ki > idx) next[ki - 1] = v;
      });
      return next;
    });
    setSelected((prev) => {
      const next = new Set();
      prev.forEach((i) => {
        if (i < idx) next.add(i);
        else if (i > idx) next.add(i - 1);
      });
      return next;
    });
    if (expandedIdx === idx) setExpandedIdx(null);
    else if (expandedIdx != null && expandedIdx > idx) setExpandedIdx(expandedIdx - 1);
  };

  const clearAll = () => {
    setRows(Array.from({ length: DEFAULT_ROWS }, (_, i) => blankRow(i + 1)));
    setResults({});
    setExpandedIdx(null);
    setHasComputed(false);
    setSelected(new Set());
  };

  /* ---------- v2.76 · selection ---------- */
  const toggleSelect = (idx) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };
  const allSelected = rows.length > 0 && selected.size === rows.length;
  const toggleSelectAll = () => {
    setSelected(allSelected ? new Set() : new Set(rows.map((_, i) => i)));
  };
  const clearSelection = () => setSelected(new Set());

  /* ---------- v2.76 · bulk-edit — apply one column value to every
     currently-selected row. Also invalidates results for those rows,
     matching the setCell contract. ---------- */
  const applyBulk = () => {
    if (selected.size === 0) {
      toast.error("Select at least one row first (checkbox on the left).");
      return;
    }
    const col = BULK_COLUMNS.find((c) => c.key === bulkColumn);
    if (!col) return;
    let value = bulkValue;
    if (col.type === "number") value = Number(value);
    else if (col.type === "boolean") value = Boolean(value);
    else if (col.type === "select" && typeof col.options?.[0]?.value === "number") {
      value = Number(value);
    }
    const patch = { [col.key]: value };
    // Keep load_amps in sync with load_value when unit is A (matches setCell).
    setRows((prev) => prev.map((r, i) => {
      if (!selected.has(i)) return r;
      const nextR = { ...r, ...patch };
      if (col.key === "load_value" && r.load_unit === "A") nextR.load_amps = Number(value);
      if (col.key === "load_unit" && value === "A") nextR.load_amps = Number(r.load_value);
      if (col.key === "continuous_factor") nextR.continuous = Number(value) >= 1.25;
      return nextR;
    }));
    setResults((prev) => {
      const next = { ...prev };
      selected.forEach((i) => { delete next[i]; });
      return next;
    });
    toast.success(`Applied ${col.label} to ${selected.size} row${selected.size > 1 ? "s" : ""}`);
  };

  /* ---------- v2.76 · CSV import / template (pure helpers in ./batch/csvHandlers) ---------- */
  const downloadCsvTemplate = () => {
    downloadBlob(buildTemplateCsvBlob(), "batch_conductor_template.csv");
  };

  const importCsv = async (file) => {
    if (!file) return;
    try {
      const text = await file.text();
      const { ok, rows: imported, unknownColumns, error } = parseCsvToRows(text);
      if (!ok) { toast.error(error); return; }
      setRows(imported);
      setResults({});
      setSelected(new Set());
      setExpandedIdx(null);
      setHasComputed(false);
      const warn = unknownColumns?.length ? ` · ignored columns: ${unknownColumns.join(", ")}` : "";
      toast.success(`Imported ${imported.length} row${imported.length > 1 ? "s" : ""} from CSV${warn}`);
    } catch (err) {
      toast.error(`CSV import failed: ${err.message || err}`);
    } finally {
      if (csvInputRef.current) csvInputRef.current.value = "";
    }
  };

  // Convert row → payload the backend expects. Mirrors ConductorRequest exactly.
  const rowsToPayload = () =>
    rows.map((r) => ({
      tag: r.tag || "",
      code: r.code,
      load_amps: r.load_unit === "A" ? Number(r.load_value) : Number(r.load_amps) || 0,
      load_value: Number(r.load_value),
      load_unit: r.load_unit,
      continuous: r.continuous,
      continuous_factor: Number(r.continuous_factor),
      temp_rating: Number(r.temp_rating),
      ambient_c: Number(r.ambient_c),
      parallel_sets: Number(r.parallel_sets),
      voltage: Number(r.voltage),
      phases: Number(r.phases),
      length_ft: Number(r.length_ft),
      power_factor: Number(r.power_factor),
      cable_type: r.cable_type,
      location_wet: r.location_wet,
      media: r.media,
      cables_in_media: Number(r.cables_in_media),
      bundled: r.bundled,
      conductor_material: r.conductor_material,
      allow_next_size_up: r.allow_next_size_up,
      conduit_material: r.conduit_material || null,
      include_neutral_in_conduit: r.include_neutral_in_conduit,
      circuit_type: r.circuit_type,
      motor_protection: r.motor_protection,
      motor_service_factor: Number(r.motor_service_factor),
      transformer_kva: r.transformer_kva ? Number(r.transformer_kva) : null,
      transformer_secondary_voltage: r.transformer_secondary_voltage
        ? Number(r.transformer_secondary_voltage) : null,
      transformer_side: r.transformer_side,
      transformer_has_secondary_protection: r.transformer_has_secondary_protection,
      transformer_supervised: r.transformer_supervised,
      transformer_primary_length_ft: r.transformer_primary_length_ft,
      transformer_secondary_length_ft: r.transformer_secondary_length_ft,
      transformer_size_both_sides: r.transformer_size_both_sides,
      motor_group_motors: r.motor_group_motors,
      motor_group_other_loads_amps: Number(r.motor_group_other_loads_amps || 0),
    }));

  const computeBatch = async () => {
    setBusy(true);
    try {
      const payload = {
        rows: rowsToPayload(),
        project_name: projectName || "—",
        engineer_name: engineerName,
        engineer_pe_license: engineerPeLicense,
      };
      const { data } = await api.post("/calculators/conductor-sizing/batch", payload);
      const map = {};
      (data.rows || []).forEach((r, i) => { map[i] = r; });
      setResults(map);
      setHasComputed(true);
      const okCount = data.rows.filter((r) => r.ok).length;
      const errCount = data.rows.length - okCount;
      if (errCount === 0) toast.success(`Computed ${okCount} circuits`);
      else toast.warning(`Computed ${okCount} · ${errCount} error${errCount > 1 ? "s" : ""} — check red rows`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const downloadBatchPdf = async () => {
    setPdfBusy(true);
    try {
      const payload = {
        rows: rowsToPayload(),
        project_name: projectName || "—",
        engineer_name: engineerName,
        engineer_pe_license: engineerPeLicense,
      };
      const res = await api.post(
        "/calculators/conductor-sizing/batch-pe-sheet",
        payload,
        { responseType: "blob" },
      );
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safe = (projectName || "batch").replace(/[^a-zA-Z0-9._-]+/g, "_").slice(0, 40) || "batch";
      a.download = `pe_calc_batch_${safe}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Combined PE calc PDF downloaded");
    } catch {
      toast.error("Failed to render combined PE sheet");
    } finally {
      setPdfBusy(false);
    }
  };

  /* v2.77 · Export results as CSV — one row per circuit with the full
     input echo + all major sized outputs. Meant to be dropped straight
     into a conductor schedule spreadsheet or handed to the drafters. */
  const exportResultsCsv = () => {
    const successful = Object.values(results).some((r) => r?.ok);
    if (!successful) {
      toast.error("Compute the batch first — nothing to export.");
      return;
    }
    const safe = (projectName || "batch").replace(/[^a-zA-Z0-9._-]+/g, "_").slice(0, 40) || "batch";
    downloadBlob(buildResultsCsvBlob(rows, results), `conductor_schedule_${safe}.csv`);
    toast.success(`Exported ${Object.values(results).filter((x) => x?.ok).length} rows to CSV`);
  };

  /* ---------- v2.78 · save / load / delete saved batches ---------- */
  const saveToProject = async () => {
    if (!projectId) {
      toast.error("Pick a project first (top of the card).");
      return;
    }
    setSavingBatch(true);
    try {
      // Persist the full raw row set (matches ConductorBatchRow schema) plus
      // the last-compute results echo so we can restore result cells too.
      const payloadRows = rowsToPayload();
      const payloadResults = rows.map((_, i) => results[i] || null);
      const { data } = await api.post("/calculators/conductor-sizing/batches", {
        project_id: projectId,
        project_name: projectName || "—",
        engineer_name: engineerName,
        engineer_pe_license: engineerPeLicense,
        rows: payloadRows,
        results: payloadResults,
      });
      toast.success(`Saved · ${payloadRows.length} row${payloadRows.length > 1 ? "s" : ""} tucked under project`);
      await refreshSavedBatches(projectId);
      return data;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setSavingBatch(false);
    }
  };

  const loadSavedBatch = async (batchId) => {
    if (!batchId) return;
    setLoadingBatchId(batchId);
    try {
      const { data } = await api.get(`/calculators/conductor-sizing/batches/${batchId}`);
      // Merge saved rows onto the blank defaults so any newer fields are
      // still populated. Preserve tag ordering.
      const loadedRows = (data.rows || []).map((r, i) => ({ ...blankRow(i + 1), ...r }));
      setRows(loadedRows.length ? loadedRows : Array.from({ length: DEFAULT_ROWS }, (_, i) => blankRow(i + 1)));
      // Restore results if the saved batch included them (best-effort).
      const nextResults = {};
      (data.results || []).forEach((r, i) => { if (r) nextResults[i] = r; });
      setResults(nextResults);
      setHasComputed(Object.keys(nextResults).length > 0);
      setSelected(new Set());
      setExpandedIdx(null);
      if (data.project_name) setProjectName(data.project_name);
      if (data.engineer_name) setEngineerName(data.engineer_name);
      if (data.engineer_pe_license) setEngineerPeLicense(data.engineer_pe_license);
      // v2.79 · Also pin the project picker so the saved-batches list
      // populates for follow-up saves under the same project.
      if (data.project_id) setProjectId(data.project_id);
      toast.success(`Loaded ${loadedRows.length} row${loadedRows.length > 1 ? "s" : ""} from saved batch`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Load failed");
    } finally {
      setLoadingBatchId("");
    }
  };

  const deleteSavedBatch = async (batchId) => {
    if (!batchId) return;
    if (!window.confirm("Delete this saved batch? This cannot be undone.")) return;
    try {
      await api.delete(`/calculators/conductor-sizing/batches/${batchId}`);
      toast.success("Saved batch deleted");
      await refreshSavedBatches(projectId);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  // v2.79 · Auto-load a saved batch when the URL says `?batch=<id>` —
  // the ref guard makes sure we don't re-fetch on unrelated re-renders.
  useEffect(() => {
    if (!autoLoadBatchId) return;
    loadSavedBatch(autoLoadBatchId);
  }, [autoLoadBatchId]);

  const summary = useMemo(() => {
    const total = rows.length;
    const computed = Object.values(results).filter((r) => r?.ok).length;
    const errors = Object.values(results).filter((r) => r && !r.ok).length;
    return { total, computed, errors };
  }, [rows.length, results]);

  return (
    <div className="border border-white/10 bg-[#12151C] p-6" data-testid="batch-conductor-card">
      <div className="flex items-center gap-2 mb-1">
        <Table weight="fill" size={18} className="text-[#00E5FF]" />
        <div className="label-tag">// Batch conductor sizing</div>
      </div>
      <h3 className="font-display text-2xl font-bold">Multi-circuit compute · PE sheet</h3>
      <p className="text-sm text-gray-400 mt-1">
        Spreadsheet-style editor. Check rows on the left to <span className="text-[#FFCC00]">bulk-apply</span> a value to any column. Click ▸ to expand a row for motor / transformer / motor-group specific NEC inputs. <span className="text-[#00E5FF]">Import CSV</span> to populate the table from a spreadsheet export. One request computes every row, one download stitches a single combined PE calc PDF.
      </p>

      {/* Project / PE metadata row */}
      <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-3">
        <LabeledInput label="Project name" value={projectName} onChange={setProjectName}
          placeholder="MCC-01 Substation Feeders" testid="batch-project-name" />
        <LabeledInput label="Reviewing engineer" value={engineerName} onChange={setEngineerName}
          placeholder="J. Smith, PE" testid="batch-engineer-name" />
        <LabeledInput label="PE license #" value={engineerPeLicense} onChange={setEngineerPeLicense}
          placeholder="e.g. TX 123456" testid="batch-engineer-license" />
      </div>

      {/* v2.78 · Project persistence — save / load saved batches */}
      <div className="mt-5 border border-[#00E5FF]/20 bg-[#00E5FF]/[0.03] p-3" data-testid="batch-project-panel">
        <div className="flex items-center gap-2 mb-2">
          <FloppyDisk size={14} className="text-[#00E5FF]" />
          <div className="label-tag">// save + load batches under a project</div>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex-1 min-w-[240px]">
            <span className="label-tag block mb-1">Project</span>
            <select value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              className="w-full bg-black/40 border border-white/10 px-2 py-2 text-sm font-mono focus:border-[#00E5FF] outline-none"
              data-testid="batch-project-select">
              <option value="">— pick a project —</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </label>
          <button onClick={saveToProject}
            disabled={!projectId || savingBatch || rows.length === 0}
            className="border border-[#00E5FF]/40 text-[#00E5FF] px-4 py-2 text-sm font-mono hover:bg-[#00E5FF]/10 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
            data-testid="batch-save-to-project"
            title={projectId
              ? "Persist the current rows + last-compute results under this project."
              : "Pick a project first."}>
            <FloppyDisk size={14} /> {savingBatch ? "Saving…" : "Save to project"}
          </button>
        </div>
        {projectId && (
          <div className="mt-3" data-testid="batch-saved-list">
            <div className="text-[10px] font-mono text-gray-500 mb-1">
              {savedBatches.length === 0
                ? "No saved batches yet for this project. Save one above to reopen next session."
                : `${savedBatches.length} saved batch${savedBatches.length > 1 ? "es" : ""}:`}
            </div>
            {savedBatches.length > 0 && (
              <div className="border border-white/10 divide-y divide-white/5 max-h-40 overflow-auto">
                {savedBatches.map((b) => (
                  <div key={b.id}
                    className="flex items-center gap-3 px-3 py-2 hover:bg-white/[0.03] text-xs font-mono"
                    data-testid={`batch-saved-${b.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-white truncate">{b.project_name || "—"}</div>
                      <div className="text-[10px] text-gray-500">
                        {b.row_count} row{b.row_count > 1 ? "s" : ""}
                        {b.result_count > 0 && <span className="text-[#00E5FF]"> · {b.result_count} computed</span>}
                        {b.engineer_name && <span> · {b.engineer_name}</span>}
                        <span> · {new Date(b.created_at).toLocaleString()}</span>
                      </div>
                    </div>
                    <button onClick={() => loadSavedBatch(b.id)}
                      disabled={loadingBatchId === b.id}
                      className="border border-[#00E5FF]/40 text-[#00E5FF] px-2 py-1 text-[11px] hover:bg-[#00E5FF]/10 disabled:opacity-40 inline-flex items-center gap-1"
                      data-testid={`batch-saved-${b.id}-load`}>
                      <ArrowClockwise size={12} /> {loadingBatchId === b.id ? "Loading…" : "Load"}
                    </button>
                    <button onClick={() => deleteSavedBatch(b.id)}
                      className="text-gray-500 hover:text-[#FF3B30] px-1"
                      data-testid={`batch-saved-${b.id}-delete`}
                      title="Delete this saved batch">
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Toolbar */}
      <div className="mt-5 flex flex-wrap items-center gap-3">
        <button onClick={addRow} className="border border-white/20 text-white px-3 py-1.5 text-xs font-mono hover:bg-white/5 inline-flex items-center gap-1.5"
          data-testid="batch-add-row">
          <Plus size={14} /> Add row
        </button>
        <button onClick={() => csvInputRef.current?.click()}
          className="border border-[#00E5FF]/40 text-[#00E5FF] px-3 py-1.5 text-xs font-mono hover:bg-[#00E5FF]/10 inline-flex items-center gap-1.5"
          data-testid="batch-csv-import-btn">
          <Upload size={14} /> Import CSV
        </button>
        <input
          ref={csvInputRef}
          type="file"
          accept=".csv,text/csv"
          onChange={(e) => importCsv(e.target.files?.[0])}
          className="hidden"
          data-testid="batch-csv-input"
        />
        <button onClick={downloadCsvTemplate}
          className="border border-white/10 text-gray-300 px-3 py-1.5 text-xs font-mono hover:bg-white/5 inline-flex items-center gap-1.5"
          data-testid="batch-csv-template">
          <FileArrowDown size={14} /> CSV template
        </button>
        <button onClick={clearAll} className="border border-white/10 text-gray-400 px-3 py-1.5 text-xs font-mono hover:bg-white/5"
          data-testid="batch-clear-all">
          Reset ({DEFAULT_ROWS} blank rows)
        </button>
        <div className="ml-auto text-xs font-mono text-gray-500" data-testid="batch-summary">
          {summary.total} rows · {summary.computed} computed
          {summary.errors > 0 && <span className="text-[#FF8A85]"> · {summary.errors} error{summary.errors > 1 ? "s" : ""}</span>}
        </div>
      </div>

      {/* v2.76 · Bulk-edit toolbar — appears only when ≥1 row is selected */}
      {selected.size > 0 && (
        <div className="mt-3 border border-[#FFCC00]/40 bg-[#FFCC00]/5 px-3 py-2 flex flex-wrap items-center gap-2"
          data-testid="batch-bulk-toolbar">
          <span className="text-xs font-mono text-[#FFCC00]" data-testid="batch-selection-count">
            {selected.size} selected
          </span>
          <span className="text-xs font-mono text-gray-400">· apply to selected:</span>
          <select value={bulkColumn} onChange={(e) => setBulkColumn(e.target.value)}
            className="bg-black/40 border border-white/10 px-2 py-1 text-xs font-mono focus:border-[#00E5FF] outline-none"
            data-testid="batch-bulk-column">
            {BULK_COLUMNS.map((c) => (
              <option key={c.key} value={c.key}>{c.label}</option>
            ))}
          </select>
          <BulkValueEditor
            column={BULK_COLUMNS.find((c) => c.key === bulkColumn)}
            value={bulkValue}
            onChange={setBulkValue}
            cableTypes={cableTypes}
          />
          <button onClick={applyBulk}
            className="border border-[#FFCC00]/50 text-[#FFCC00] px-3 py-1 text-xs font-mono hover:bg-[#FFCC00]/10"
            data-testid="batch-bulk-apply">
            Apply to {selected.size}
          </button>
          <button onClick={clearSelection}
            className="text-xs font-mono text-gray-400 hover:text-white ml-auto"
            data-testid="batch-bulk-clear">
            Clear selection
          </button>
        </div>
      )}

      {/* Spreadsheet body */}
      <div className="mt-3 border border-white/10 overflow-x-auto" data-testid="batch-table-wrap">
        <table className="w-full text-xs font-mono">
          <thead className="bg-[#0A0C10] text-gray-400">
            <tr className="border-b border-white/10">
              <Th className="w-8">
                <input type="checkbox"
                  checked={allSelected}
                  onChange={toggleSelectAll}
                  data-testid="batch-select-all"
                  title="Select / deselect all rows" />
              </Th>
              <Th className="w-8"></Th>
              <Th>Tag</Th>
              <Th>Circuit type</Th>
              <Th className="w-24">Load</Th>
              <Th className="w-16">Unit</Th>
              <Th className="w-20">Cont ×</Th>
              <Th className="w-20">V</Th>
              <Th className="w-16">φ</Th>
              <Th className="w-20">Length ft</Th>
              <Th className="w-16">Temp</Th>
              <Th className="w-16">Amb °C</Th>
              <Th className="w-16">Par</Th>
              <Th>Cable</Th>
              <Th className="w-16">Mat</Th>
              <Th className="w-20">Media</Th>
              <Th className="w-20">Conduit</Th>
              <Th className="w-14">240.4(B)</Th>
              <Th className="w-40">Result</Th>
              <Th className="w-16"></Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => {
              const res = results[idx];
              const expanded = expandedIdx === idx;
              const rowErr = res && !res.ok;
              const isSelected = selected.has(idx);
              return (
                <Fragment key={`row-${idx}`}>
                  <tr
                      className={`border-b border-white/5 ${rowErr ? "bg-[#FF3B30]/10" : isSelected ? "bg-[#FFCC00]/10 hover:bg-[#FFCC00]/15" : "hover:bg-white/[0.02]"}`}
                      data-testid={`batch-row-${idx}`}>
                    <td className="p-1 text-center">
                      <input type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelect(idx)}
                        data-testid={`batch-row-${idx}-select`} />
                    </td>
                    <td className="p-1 text-center">
                      <button onClick={() => setExpandedIdx(expanded ? null : idx)}
                        className="text-gray-400 hover:text-[#00E5FF]"
                        data-testid={`batch-row-${idx}-expand`}
                        title="Expand row for mode-specific NEC inputs">
                        {expanded ? <CaretDown size={12} /> : <CaretRight size={12} />}
                      </button>
                    </td>
                    <Td>
                      <CellInput value={r.tag} onChange={(v) => setCell(idx, { tag: v })}
                        className="w-20" testid={`batch-row-${idx}-tag`} />
                    </Td>
                    <Td>
                      <CellSelect value={r.circuit_type} onChange={(v) => setCell(idx, { circuit_type: v })}
                        options={CIRCUIT_TYPES} className="w-40" testid={`batch-row-${idx}-circuit-type`} />
                    </Td>
                    <Td>
                      <CellInput type="number" value={r.load_value}
                        onChange={(v) => setCell(idx, {
                          load_value: Number(v),
                          load_amps: r.load_unit === "A" ? Number(v) : r.load_amps,
                        })}
                        className="w-20" testid={`batch-row-${idx}-load`} />
                    </Td>
                    <Td>
                      <CellSelect value={r.load_unit}
                        onChange={(v) => setCell(idx, { load_unit: v })}
                        options={LOAD_UNITS.map((u) => ({ value: u, label: u }))}
                        className="w-14" testid={`batch-row-${idx}-load-unit`} />
                    </Td>
                    <Td>
                      <CellSelect value={r.continuous_factor}
                        onChange={(v) => setCell(idx, {
                          continuous_factor: Number(v),
                          continuous: Number(v) >= 1.25,
                        })}
                        options={[
                          { value: 1.0, label: "1.00" },
                          { value: 1.25, label: "1.25" },
                        ]}
                        className="w-16" testid={`batch-row-${idx}-cont`} />
                    </Td>
                    <Td>
                      <CellInput type="number" value={r.voltage}
                        onChange={(v) => setCell(idx, { voltage: Number(v) })}
                        className="w-16" testid={`batch-row-${idx}-voltage`} />
                    </Td>
                    <Td>
                      <CellSelect value={r.phases}
                        onChange={(v) => setCell(idx, { phases: Number(v) })}
                        options={[{ value: 1, label: "1" }, { value: 3, label: "3" }]}
                        className="w-14" testid={`batch-row-${idx}-phases`} />
                    </Td>
                    <Td>
                      <CellInput type="number" value={r.length_ft}
                        onChange={(v) => setCell(idx, { length_ft: Number(v) })}
                        className="w-16" testid={`batch-row-${idx}-length`} />
                    </Td>
                    <Td>
                      <CellSelect value={r.temp_rating}
                        onChange={(v) => setCell(idx, { temp_rating: Number(v) })}
                        options={TEMP_RATINGS.map((t) => ({ value: t, label: `${t}°C` }))}
                        className="w-16" testid={`batch-row-${idx}-temp`} />
                    </Td>
                    <Td>
                      <CellInput type="number" value={r.ambient_c}
                        onChange={(v) => setCell(idx, { ambient_c: Number(v) })}
                        className="w-14" testid={`batch-row-${idx}-ambient`} />
                    </Td>
                    <Td>
                      <CellInput type="number" value={r.parallel_sets}
                        onChange={(v) => setCell(idx, { parallel_sets: Math.max(1, Number(v)) })}
                        className="w-14" testid={`batch-row-${idx}-par`} />
                    </Td>
                    <Td>
                      <CellSelect value={r.cable_type}
                        onChange={(v) => setCell(idx, { cable_type: v })}
                        options={(cableTypes.length ? cableTypes : [{ key: "THWN-2" }])
                          .map((c) => {
                            const k = typeof c === "string" ? c : (c.key || c.id);
                            return { value: k, label: k };
                          })}
                        className="w-24" testid={`batch-row-${idx}-cable`} />
                    </Td>
                    <Td>
                      <CellSelect value={r.conductor_material}
                        onChange={(v) => setCell(idx, { conductor_material: v })}
                        options={[
                          { value: "copper", label: "Cu" },
                          { value: "aluminum", label: "Al" },
                        ]}
                        className="w-14" testid={`batch-row-${idx}-mat`} />
                    </Td>
                    <Td>
                      <CellSelect value={r.media}
                        onChange={(v) => setCell(idx, { media: v })}
                        options={MEDIA_OPTIONS.map((m) => ({ value: m, label: m }))}
                        className="w-20" testid={`batch-row-${idx}-media`} />
                    </Td>
                    <Td>
                      <CellSelect value={r.conduit_material}
                        onChange={(v) => setCell(idx, { conduit_material: v })}
                        options={CONDUIT_MATERIALS.map((c) => ({ value: c, label: c || "—" }))}
                        className="w-20" testid={`batch-row-${idx}-conduit`} />
                    </Td>
                    <Td className="text-center">
                      <input type="checkbox" checked={r.allow_next_size_up}
                        onChange={(e) => setCell(idx, { allow_next_size_up: e.target.checked })}
                        data-testid={`batch-row-${idx}-nsu`} />
                    </Td>
                    <Td>
                      <ResultCell res={res} hasComputed={hasComputed} testid={`batch-row-${idx}-result`} />
                    </Td>
                    <Td>
                      <div className="flex gap-1">
                        <button onClick={() => duplicateRow(idx)}
                          className="text-gray-400 hover:text-[#00E5FF]"
                          data-testid={`batch-row-${idx}-duplicate`} title="Duplicate row">
                          <Copy size={12} />
                        </button>
                        <button onClick={() => removeRow(idx)}
                          disabled={rows.length <= 1}
                          className="text-gray-400 hover:text-[#FF3B30] disabled:opacity-30"
                          data-testid={`batch-row-${idx}-remove`} title="Remove row">
                          <Trash size={12} />
                        </button>
                      </div>
                    </Td>
                  </tr>
                  {expanded && (
                    <tr
                        className="bg-[#0A0C10] border-b border-white/10"
                        data-testid={`batch-row-${idx}-details`}>
                      <td colSpan={20} className="p-4">
                        <RowExpanded row={r} setCell={(patch) => setCell(idx, patch)} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Compute + Download row */}
      <div className="mt-5 flex flex-wrap gap-3">
        <button onClick={computeBatch} disabled={busy || rows.length === 0}
          className="btn-primary inline-flex items-center gap-2"
          data-testid="batch-compute">
          <Calculator size={16} /> {busy ? "Computing…" : `Compute all (${rows.length})`}
        </button>
        <button onClick={downloadBatchPdf}
          disabled={pdfBusy || !hasComputed || summary.errors === rows.length}
          className="border border-[#00E5FF]/40 text-[#00E5FF] px-4 py-2 text-sm font-mono hover:bg-[#00E5FF]/10 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
          data-testid="batch-download-pdf"
          title={hasComputed
            ? "Download a single combined PE calc PDF (summary table + per-circuit audit sheets)."
            : "Compute the batch first — the PDF always reflects the current live results."}>
          <Download size={14} /> {pdfBusy ? "Rendering…" : "Combined PE calc PDF"}
        </button>
        <button onClick={exportResultsCsv}
          disabled={!hasComputed || summary.computed === 0}
          className="border border-white/20 text-white px-4 py-2 text-sm font-mono hover:bg-white/5 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
          data-testid="batch-export-csv"
          title={summary.computed > 0
            ? "Export a conductor schedule CSV — full inputs + selected size, ampacity, breaker, VD %, motor / TX / group OCPD, conduit trade size — one row per circuit."
            : "Compute the batch first — CSV mirrors the live results."}>
          <FileArrowDown size={14} /> Results CSV
        </button>
      </div>

      <div className="mt-6 text-[10px] text-gray-500 font-mono">
        // Batch compute uses the same NEC 310.16 / 240.4 / 430.52 / 430.24 / 450.3 rules as the single-circuit tool. Each row is independent — one row erroring will not stop the others.
      </div>
    </div>
  );
}

